import { Alert, Form, Spin } from 'antd';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAsyncCallback } from 'react-async-hook';
import { familyService } from 'services';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { FAMILY_APPLICATION_STEPS, CHILD_GRADE_LEVELS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { Banner } from 'components/ui/Banner';
import RadioCards from 'components/RadioCards/RadioCards';
import AddressFields from 'components/AddressFields/AddressFields';
import Select from 'components/Select';
import { Input } from 'antd';
import Card from 'components/Card/Card';
import { RevealingLink } from 'components/ui/RevealingLink/RevealingLink';
import FormHelperText from 'components/ui/FormHelperText/FormHelperText';
import dayjs from 'dayjs';
import { getFirstName } from 'utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationChildrenEducationalStatus({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const location = useLocation();

  // Form state
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const gradeFormValue = Form.useWatch('grade', form);
  const inSchoolFormValue = Form.useWatch('inSchool', form);

  // Get entity index from query parameters
  const urlParams = new URLSearchParams(location.search);
  const currentChildIndex = parseInt(urlParams.get('entity') || '0', 10);

  const currentChild = application?.children?.[currentChildIndex];
  const childFirstName = getFirstName({ firstName: currentChild?.firstName });

  // Check if current date is in summer period (June 1 - August 31)
  const isSummerPeriod = useMemo(() => {
    const today = dayjs();
    const june1 = dayjs().month(5).date(1);
    const august31 = dayjs().month(7).date(31);

    return today.isAfter(june1.subtract(1, 'day')) && today.isBefore(august31.add(1, 'day'));
  }, []);

  // Track previously stored education IDs
  const prevEducationIdsRef = useRef([]);

  // Fetch education data
  const { loading: loadingEducation, execute: fetchEducation } = useAsyncCallback(async () => {
    if (!currentChild?.id) return [];

    try {
      const data = await familyService.getSchoolInfo(currentChild.id);

      // Set the form values
      const educationItem = data?.[0];
      if (educationItem) {
        form.setFieldsValue({
          grade: { value: educationItem.grade, label: educationItem.grade },
          schoolName: educationItem.schoolName,
          schoolAddress: educationItem.address,
          endDate: educationItem.endDate,
          startDate: educationItem.startDate,
        });
        prevEducationIdsRef.current = [educationItem.id];
      } else {
        prevEducationIdsRef.current = [];
      }

      return data;
    } catch (error) {
      console.error('Error fetching education data:', error);
      return [];
    }
  });

  // Fetch education data when child ID changes
  useEffect(() => {
    if (currentChild?.id) {
      fetchEducation();
    }
  }, [fetchEducation, currentChild?.id]);

  // Initialize form with existing data
  useEffect(() => {
    if (currentChild) {
      form.setFieldsValue({
        inSchool: currentChild.inSchool,
      });
    }
  }, [currentChild, form]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Navigate to next step
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_EDUCATIONAL_STATUS,
          _newApplication,
          stepConfig,
          currentChildIndex,
        );
        setStep({
          applicationId: _newApplication.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        // Update child with inSchool status
        const childData = {
          ...currentChild,
          inSchool: values.inSchool,
        };

        // Save child first
        const savedChild = await familyService.addUpdateFamilyChild(household?.id, currentChild?.id || null, childData);

        // Handle education data
        const prevIds = prevEducationIdsRef.current;

        if (!values.inSchool && prevIds.length > 0) {
          // Delete all previous educations if not in school
          for (const id of prevIds) {
            await familyService.deleteSchoolInfo(savedChild.id, id, { id });
          }
        } else if (values.inSchool) {
          // Create/update education item
          const educationData = {
            grade: values.grade?.value,
            schoolName: values.schoolName,
            address: values.schoolAddress,
            endDate: values.endDate,
            startDate: values.startDate,
          };

          if (prevIds.length > 0) {
            educationData.id = prevIds[0];
          }

          await familyService.addUpdateSchoolInfo(savedChild.id, [educationData]);
        }

        // Update application with the saved child
        const updatedChildren = [...(application.children || [])];
        updatedChildren[currentChildIndex] = savedChild;

        const updatedApplication = {
          ...application,
          children: updatedChildren,
        };

        const savedApplication = await familyService.saveApplication(updatedApplication);
        setApplication(savedApplication);

        _goToNextStep(savedApplication);
      } catch (error) {
        console.error('Error saving child education:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, currentChild, currentChildIndex, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  if (loading || loadingEducation) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={`${childFirstName} - Educational Status`} />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <div className="space-y-6">
            <Banner question={`Is ${childFirstName} in school?`} className="my-12" />

            {/* Is child in school question */}
            <Form.Item
              name="inSchool"
              rules={[{ required: true, message: 'Please select if the child is enrolled in school' }]}
            >
              <RadioCards
                options={[
                  {
                    value: true,
                    label: 'Yes',
                  },
                  {
                    value: false,
                    label: 'No',
                  },
                ]}
                data-testid="child-in-school-cards"
              />
            </Form.Item>

            {inSchoolFormValue === true && (
              <>
                <div className="space-y-10">
                  {/* Grade Level */}
                  <div>
                    <Form.Item
                      name="grade"
                      label="Grade Level"
                      rules={[{ required: true, message: 'Please select a grade level' }]}
                      className="mb-0"
                    >
                      <Select
                        placeholder="Select grade level"
                        options={CHILD_GRADE_LEVELS}
                        getOptionLabel={(opt) => opt.label}
                        getOptionValue={(opt) => opt.value}
                        isOptionSelected={(opt) => opt.value === gradeFormValue?.value}
                        value={gradeFormValue}
                        data-testid="grade-level-select"
                      />
                    </Form.Item>

                    {isSummerPeriod && <FormHelperText text="Select the grade they're entering this fall" />}
                  </div>

                  {/* School Name */}
                  <Form.Item
                    name="schoolName"
                    label="School/Institution Name"
                    rules={[{ required: true, message: 'Please enter the school name' }]}
                  >
                    <Input placeholder="Enter school name" data-testid="school-name-input" />
                  </Form.Item>

                  {/* School Address */}
                  <Card title="School Address">
                    <AddressFields
                      addressKey="schoolAddress"
                      data-testid="school-address-fields"
                      labels={{
                        street2: 'Line 2 (Optional)',
                      }}
                    />

                    <RevealingLink
                      text="Why do we need this?"
                      reveals={
                        <Alert
                          type="info"
                          showIcon
                          description="We use school location to find nearby providers and verify eligibility for school-based programs"
                        />
                      }
                    />
                  </Card>

                  {/* Critical School Dates */}
                  {/* <Card
                    color="error"
                    title={
                      <div className="flex items-center gap-3">
                        <WarningAltFilled className="w-5 h-5 mt-[-1px]" />
                        <span>Critical School Dates</span>
                      </div>
                    }
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Form.Item
                        name="endDate"
                        label="When does school end?"
                        rules={[{ required: true, message: 'Please select school end date' }]}
                        className="mb-0"
                      >
                        <DatePicker data-testid="school-end-date" />
                      </Form.Item>

                      <Form.Item
                        name="startDate"
                        label="When does school start again?"
                        rules={[{ required: true, message: 'Please select school start date' }]}
                        className="mb-0"
                      >
                        <DatePicker data-testid="school-start-date" />
                      </Form.Item>
                    </div>

                    <Alert
                      showIcon
                      message="Already on summer break?"
                      description="Just put your best guess for when school ended - early June is fine. The important date is when school starts again"
                      className="my-4"
                    />

                    <RevealingLink
                      text="Why do we need these dates?"
                      linkColor="error"
                      reveals={
                        <Alert
                          type="error"
                          message="Important"
                          description={
                            <>
                              <p>
                                Fee assistance for school-age children is different during school year and summer break.
                              </p>

                              <p className="mt-4">
                                During the school year, fee assistance is limited to before/after school hours. During
                                summer break, full-day assistance is available. Providing accurate school dates ensures
                                your benefits are calculated correctly.
                              </p>
                            </>
                          }
                        />
                      }
                    />
                  </Card> */}
                </div>
              </>
            )}
          </div>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_EDUCATIONAL_STATUS,
                application,
                stepConfig,
                currentChildIndex,
              );
              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
            disabled: submitting,
            'data-testid': 'children-educational-status-back-button',
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'children-educational-status-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
